import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Text,
  Group,
  Select,
  TextInput,
  Stack,
  Badge,
  ActionIcon,
  Tooltip,
  Button,
} from '@mantine/core';
import { IconSearch, IconArrowUp, IconArrowDown, IconX, IconEye, IconDownload } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';
import { getUserBetSlips } from '../services/api';
import { BetSlipWithSelections, BetSlipStatus, mapBetSlipData } from '../types/betslip';
import { BetSlipDetails } from '../components/BetSlipDetails';
import { ExportDialog } from '../components/ExportDialog';

const statusColors: Record<BetSlipStatus, string> = {
  pending: 'yellow',
  won: 'green',
  lost: 'red',
  push: 'gray',
  cancelled: 'gray',
};

export default function BetsList() {
  const { user } = useAuth();
  const [betSlips, setBetSlips] = useState<Partial<BetSlipWithSelections>[]>([]);
  const [filteredBetSlips, setFilteredBetSlips] = useState<Partial<BetSlipWithSelections>[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [selectedBetSlip, setSelectedBetSlip] = useState<Partial<BetSlipWithSelections> | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  useEffect(() => {
    const fetchBetSlips = async () => {
      if (user?.id) {
        const data = await getUserBetSlips(user.id);
        const mappedData = mapBetSlipData(data);
        setBetSlips(mappedData);
        applyFilters(mappedData, statusFilter, searchQuery, sortBy);
      }
    };

    fetchBetSlips();
  }, [user]);

  const applyFilters = (
    data: Partial<BetSlipWithSelections>[],
    status: string | null,
    search: string,
    sort: string | null
  ) => {
    let filtered = [...data];

    // Apply status filter
    if (status) {
      filtered = filtered.filter((slip) => slip.status === status);
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (slip) =>
          slip.ticketId?.toLowerCase().includes(searchLower) ||
          slip.casinoName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    if (sort) {
      filtered.sort((a, b) => {
        if (!a.eventDateTime || !b.eventDateTime) return 0;
        const dateA = new Date(a.eventDateTime);
        const dateB = new Date(b.eventDateTime);
        return sort === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
      });
    }

    setFilteredBetSlips(filtered);
  };

  const handleStatusFilterChange = (value: string | null) => {
    setStatusFilter(value);
    applyFilters(betSlips, value, searchQuery, sortBy);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    applyFilters(betSlips, statusFilter, value, sortBy);
  };

  const handleSortChange = (value: string | null) => {
    setSortBy(value);
    applyFilters(betSlips, statusFilter, searchQuery, value);
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setSearchQuery('');
    setSortBy(null);
    setFilteredBetSlips(betSlips);
  };

  const handleBetSlipUpdate = async () => {
    if (user?.id) {
      const data = await getUserBetSlips(user.id);
      const mappedData = mapBetSlipData(data);
      setBetSlips(mappedData);
      applyFilters(mappedData, statusFilter, searchQuery, sortBy);

      // Update the selected bet slip if it exists
      if (selectedBetSlip) {
        const updatedBetSlip = mappedData.find(slip => slip.id === selectedBetSlip.id);
        if (updatedBetSlip) {
          setSelectedBetSlip(updatedBetSlip);
        }
      }
    }
  };

  return (
    <Container size="xl">
      <Title order={2} mb="xl">
        My Bet Slips
      </Title>

      <Paper shadow="xs" p="md" mb="xl">
        <Group position="apart" mb="md">
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'won', label: 'Won' },
              { value: 'lost', label: 'Lost' },
              { value: 'push', label: 'Push' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
            style={{ width: 200 }}
          />

          <TextInput
            placeholder="Search by ticket ID or casino"
            value={searchQuery}
            onChange={(event) => handleSearchChange(event.currentTarget.value)}
            icon={<IconSearch size={14} />}
            style={{ width: 300 }}
          />

          <Group>
            <Group spacing="xs">
              <Select
                placeholder="Sort by date"
                value={sortBy}
                onChange={handleSortChange}
                data={[
                  { value: 'asc', label: 'Oldest first' },
                  { value: 'desc', label: 'Newest first' },
                ]}
                style={{ width: 150 }}
              />

              {(statusFilter || searchQuery || sortBy) && (
                <Tooltip label="Clear filters">
                  <ActionIcon onClick={clearFilters} variant="light">
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>

            <Button
              variant="light"
              leftIcon={<IconDownload size={16} />}
              onClick={() => setExportDialogOpen(true)}
            >
              Export
            </Button>
          </Group>
        </Group>

        <Stack spacing="md">
          {filteredBetSlips.map((slip, index) => (
            <Paper key={index} shadow="xs" p="md" withBorder>
              <Group position="apart">
                <div>
                  <Group spacing="xs">
                    <Text weight={500}>Ticket ID: {slip.ticketId}</Text>
                    {slip.status && (
                      <Badge color={statusColors[slip.status as BetSlipStatus]}>
                        {slip.status.toUpperCase()}
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" color="dimmed">
                    {slip.casinoName}
                  </Text>
                  <Text size="sm">
                    Wager: ${slip.wagerAmount?.toFixed(2)} | Potential Payout: $
                    {slip.potentialPayout?.toFixed(2)}
                  </Text>
                </div>
                <Group>
                  <Group spacing="xs">
                    <Tooltip label="View details">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={() => setSelectedBetSlip(slip)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Group spacing={4} ml="lg">
                    {sortBy === 'asc' ? (
                      <IconArrowUp size={16} />
                    ) : sortBy === 'desc' ? (
                      <IconArrowDown size={16} />
                    ) : null}
                    <Text size="sm" color="dimmed">
                      {slip.eventDateTime
                        ? new Date(slip.eventDateTime).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })
                        : 'Date not available'}
                    </Text>
                  </Group>
                </Group>
              </Group>
            </Paper>
          ))}

          {filteredBetSlips.length === 0 && (
            <Text align="center" color="dimmed">
              No bet slips found matching your criteria
            </Text>
          )}
        </Stack>
      </Paper>

      {selectedBetSlip && (
        <BetSlipDetails
          betSlip={selectedBetSlip}
          opened={!!selectedBetSlip}
          onClose={() => setSelectedBetSlip(null)}
          onUpdate={handleBetSlipUpdate}
        />
      )}

      <ExportDialog
        opened={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </Container>
  );
}